import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'src/redux/index';
import type { PaymentFunnelTrigger } from 'src/helpers/paymentFunnel';
import { AddAiCreditsRequest, AppInitialState } from '../types/appState';
import { getConstants, getCurrencySymbols } from '../actions/appState';

const initialState: AppInitialState = {
  isOffline: !navigator.onLine,
  isSocketConnected: false,
  isAddAiCreditsOpen: false,
  addAiCreditsTrigger: 'out_of_credits',
  addAiCreditsNeed: null,
  addAiCreditsAction: null,
  addAiCreditsTopUp: false,
  addAiCreditsReason: null,
  isServerNotAvailable: false,
  isSideBarOpen: true,
  currencySymbols: null,
  constants: null,
};

/* eslint-disable no-param-reassign */
const appStateSlice = createSlice({
  name: 'Offline',
  initialState,
  reducers: {
    setIsOffline: (state, action: PayloadAction<boolean>) => {
      state.isOffline = action.payload;
    },
    setIsSocketConnected: (state, action: PayloadAction<boolean>) => {
      state.isSocketConnected = action.payload;
    },
    // App-wide: any feature that runs out of AI credits opens the top-up dialog
    // by dispatching this, rather than mounting its own copy. A free candidate
    // is pitched Pro first, led by `need`; a voluntary open ('browse') and
    // `topUp` go straight to the packs. The trigger is for the payment funnel.
    openAddAiCredits: (
      state,
      action: PayloadAction<
        PaymentFunnelTrigger | AddAiCreditsRequest | undefined
      >,
    ) => {
      const req: AddAiCreditsRequest =
        typeof action.payload === 'string'
          ? { trigger: action.payload }
          : (action.payload ?? {});
      state.isAddAiCreditsOpen = true;
      state.addAiCreditsTrigger = req.trigger ?? 'out_of_credits';
      state.addAiCreditsNeed = req.need ?? null;
      state.addAiCreditsAction = req.action
        ? { action: req.action, needed: req.needed }
        : null;
      state.addAiCreditsTopUp = !!req.topUp || req.trigger === 'browse';
      state.addAiCreditsReason = req.reason ?? null;
    },
    closeAddAiCredits: (state) => {
      state.isAddAiCreditsOpen = false;
    },
    setIsSideBarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSideBarOpen = action.payload;
    },
    setIsServerNotAvailable: (state, action: PayloadAction<boolean>) => {
      state.isServerNotAvailable = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(getCurrencySymbols.fulfilled, (state, action) => {
      state.currencySymbols = action.payload;
    });
    builder.addCase(getConstants.fulfilled, (state, action) => {
      state.constants = action.payload;
    });
  },
});
/* eslint-enable no-param-reassign */
export const appStateSelector = (state: RootState) => state.appState;
export const {
  openAddAiCredits,
  closeAddAiCredits,
  setIsOffline,
  setIsSocketConnected,
  setIsSideBarOpen,
  setIsServerNotAvailable,
} = appStateSlice.actions;

export default appStateSlice.reducer;
